import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Section, SectionHeading, accent, accentOrder, cn } from "../ui";

export interface PlatformStat {
  label: string;
  value: string;
}

export interface StatisticsSectionProps {
  /**
   * Rendered only when supplied. The previous version hardcoded "25,000 active
   * users", "100,000 academic resources", "2,500 courses" and "750 global
   * institutions" — none of which came from a source. The only counts the
   * backend exposes (`/admin/stats/*`) are admin-protected, so there is nothing
   * public to read yet. Pass real figures the moment a public endpoint exists
   * and the band lights up; until then it stays out of the page rather than
   * putting invented numbers in front of visitors.
   */
  stats?: PlatformStat[];
}

const StatisticsSection = ({ stats }: StatisticsSectionProps) => {
  const navigate = useNavigate();

  if (!stats || stats.length === 0) {
    return null;
  }

  return (
    <Section tone="blue">
      <SectionHeading
        align="center"
        eyebrow="Platform Growth"
        eyebrowAccent={accent('blue').rule}
        title="Our Impact In Numbers"
      />

      {/* Separate tiles rather than one hairline grid: each figure sits on its
          own card under a full-strength accent rule, so a row of numbers reads
          as a set of distinct facts instead of a spreadsheet. The accent cycles
          by position, which keeps the row colourful without attaching meaning
          to any one hue — the label always carries the meaning. */}
      <dl className="mx-auto grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const palette = accent(accentOrder[index % accentOrder.length]);

          return (
            <div
              key={stat.label}
              className="overflow-hidden rounded-lg border border-border bg-background-card text-center shadow-xs"
            >
              <span aria-hidden="true" className={cn('block h-1', palette.rule)} />
              <div className="px-5 py-8">
                <dt className="text-overline font-semibold uppercase text-text-secondary">
                  {stat.label}
                </dt>
                <dd className={cn('mt-3 font-display text-h2 tabular-nums', palette.text)}>
                  {stat.value}
                </dd>
              </div>
            </div>
          );
        })}
      </dl>

      <div className="mx-auto mt-12 flex max-w-4xl flex-col items-center gap-6 rounded-lg border border-border bg-background-card p-6 text-center sm:p-8 md:flex-row md:justify-between md:text-left">
        <div>
          <h3 className="text-h4 font-sans font-semibold text-text-primary">Growing Every Day</h3>
          <p className="mt-1 text-body text-text-secondary">
            Join our expanding academic community
          </p>
        </div>
        <Button
          onClick={() => navigate('/courses')}
          trailingIcon={<ArrowRight size={18} aria-hidden="true" />}
          className="w-full md:w-auto"
        >
          Explore courses
        </Button>
      </div>
    </Section>
  );
};

export default StatisticsSection;
